import { useMutation, useQuery } from '@tanstack/react-query';
import { messagesApi } from '@/lib/api';
import { MessageSquare } from 'lucide-react';
import { Conversation, UserProfile } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface ConversationListProps {
  conversations: Conversation[];
  onSelectConversation: (conversation: Conversation) => void;
  onViewProfile?: (userId: string) => void;
  currentUserId?: string;
}

export const ConversationList = ({ 
  conversations, 
  onSelectConversation,
  onViewProfile,
  currentUserId
}: ConversationListProps) => {
  const queryClient = useQueryClient();

  // Mutation to mark conversation as read
  const markAsReadMutation = useMutation({
    mutationFn: (conversationId: string) => messagesApi.markAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  const handleSelectConversation = (convo: Conversation) => {
    // Mark as read if there are unread messages
    if (convo.unreadCount && convo.unreadCount > 0) {
      markAsReadMutation.mutate(convo.id);
    }
    onSelectConversation(convo);
  };

  if (conversations.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No conversations yet</p>
        <p className="text-sm text-muted-foreground/60">Messages will appear here</p>
      </div>
    );
  }

  // Sort conversations: unread first, then by most recent
  const sortedConversations = [...conversations].sort((a, b) => {
    const aUnread = (a.unreadCount || 0) > 0;
    const bUnread = (b.unreadCount || 0) > 0;
    if (aUnread && !bUnread) return -1;
    if (!aUnread && bUnread) return 1;
    // If both have same read status, sort by timestamp if available
    const aTime = a.lastMessage?.timestamp || '';
    const bTime = b.lastMessage?.timestamp || '';
    return bTime.localeCompare(aTime);
  });

  return (
    <div className="space-y-2">
      {sortedConversations.map((convo) => {
        const otherUser = convo.participants.find(p => p.id !== currentUserId);
        const otherUserName = otherUser?.name || 'Unknown User';
        const otherUserInitials = otherUserName
          .split(' ')
          .map((part) => part[0])
          .filter(Boolean)
          .join('')
          .slice(0, 2)
          .toUpperCase();
        const hasUnread = (convo.unreadCount || 0) > 0;
        
        return (
          <button
            key={convo.id}
            onClick={() => handleSelectConversation(convo)}
            className={`w-full p-4 rounded-xl border transition-all flex items-center gap-3 ${
              hasUnread 
                ? 'bg-primary/5 border-primary/30 hover:bg-primary/10 hover:border-primary/50' 
                : 'bg-card hover:bg-muted/50 hover:border-primary/30'
            }`}
          >
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform ${
                hasUnread ? 'bg-primary/20' : 'bg-primary/10'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile?.(otherUser?.id || '');
              }}
              title="View profile"
            >
              <Avatar className="w-12 h-12">
                <AvatarImage src={otherUser?.avatar} alt={otherUserName} />
                <AvatarFallback className={hasUnread ? 'bg-primary/20 text-primary' : 'bg-primary/10 text-primary/70'}>
                  {otherUserInitials || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center justify-between">
                <p className={`font-medium truncate ${hasUnread ? 'text-foreground' : 'text-foreground/80'}`}>
                  {otherUserName}
                </p>
                {hasUnread && (
                  <span className="w-2.5 h-2.5 bg-primary rounded-full flex-shrink-0 ml-2" />
                )}
              </div>
              <p className={`text-sm truncate ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {convo.lastMessage?.text || 'Start chatting'}
              </p>
            </div>
            {hasUnread && (
              <span className="min-w-[24px] h-6 bg-primary text-primary-foreground text-xs font-semibold rounded-full flex items-center justify-center px-1.5">
                {convo.unreadCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ConversationList;
